import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ToastContainer, toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_BASE_URL } from '../../config';
import DataTable from 'react-data-table-component';
import Pagination from '../Pagination/Paginations';
import EntriesSelector from '../Pagination/EntriesSelector';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import DownloadButtons from '../DownloadButtons/DownloadButtons';

import Select from "react-select";



const CreateJob = () => {
  const [job, setJob] = useState({
    company_id: '',
    job_title: '',
    designation_id: '',
    job_type: 'Full Time',
    is_featured: false,
    job_vacancy: '',
    gender: 'Any',
    minimum_experience: '',
    date_of_closing: '',
    short_description: '',
    long_description: '',
    status: 1,
  });

  const [designations, setDesignations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [submittedJobs, setSubmittedJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const savingRef = useRef(false);
  const updatingRef = useRef(false);
  

  useEffect(() => {
    axios.get(`${API_BASE_URL}/designations`).then(res => setDesignations(res.data));
    axios.get(`${API_BASE_URL}/companies`).then(res => setCompanies(res.data));
    fetchJobs();
  }, []);

  const fetchJobs = () => {
    axios.get(`${API_BASE_URL}/jobs`)
      .then(res => setSubmittedJobs(res.data))
      .catch(err => console.error('Error fetching jobs:', err));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setJob({
      ...job,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    });
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  // block repeat submits while a create request is already running
  if (savingRef.current) return;

  savingRef.current = true;
  setIsSaving(true);

  try {
    const res = await axios.post(`${API_BASE_URL}/create-job`, job);

    toast.success(res.data.message);
    fetchJobs();
    setShowForm(false);

    setJob({
      company_id: '',
      job_title: '',
      designation_id: '',
      job_type: 'Full Time',
      is_featured: false,
      job_vacancy: '',
      gender: 'Any',
      minimum_experience: '',
      date_of_closing: '',
      short_description: '',
      long_description: '',
      status: 1,
    });

  } catch (error) {

    if (error.response?.status === 422) {
      toast.error(error.response.data.error, { toastId: "create-job-error" });
      return;
    }

    if (error.response?.status === 409) {
      toast.error(error.response.data.error, { toastId: "create-job-error" });
      return;
    }

    toast.error("Something went wrong", { toastId: "create-job-error" });
  } finally {
    savingRef.current = false;
    setIsSaving(false);
  }
};


  const handleEditClick = (job) => {
    setEditJob({ ...job });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditJob({
      ...editJob,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    });
  };

  const submitEdit = async () => {
    // block repeat clicks while an update request is already running
    if (updatingRef.current) return;

    updatingRef.current = true;
    setIsUpdating(true);

    try {
      await axios.put(`${API_BASE_URL}/update-job/${editJob.job_id}`, editJob);
      toast.success('Job updated successfully!');
      setShowEditModal(false);
      fetchJobs();
    } catch (err) {
      console.log("ERROR:", err.response);

      toast.error('Failed to update job.', { toastId: "update-job-error" });
    } finally {
      updatingRef.current = false;
      setIsUpdating(false);
    }
  };

  const filteredJobs = submittedJobs.filter(job => {
  const term = searchTerm.toLowerCase();
  const statusLabel = job.status === 1 ? 'active' : 'inactive';

  return (
    job.job_title.toLowerCase().includes(term) ||
    statusLabel.includes(term)
  );
});

  const totalPages = Math.ceil(filteredJobs.length / entriesPerPage);
  const indexOfLastJob = currentPage * entriesPerPage;
  const indexOfFirstJob = indexOfLastJob - entriesPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);

  const jobColumns = [
    { name: "Job ID", selector: row => row.job_id, sortable: true, width: "100px" },
    { name: "Title", selector: row => row.job_title, sortable: true, wrap: true },
    { name: "Company", selector: row => row.company_name, sortable: true, wrap: true },
    { name: "Status", selector: row => (row.status === 1 ? 'Active' : 'Inactive'), sortable: true, width: "120px" },
    { name: "Created", selector: row => row.created_at, sortable: true, width: "180px" },
    {
      name: "Action",
      width: "100px",
      cell: row => (
        <button className="btn btn-sm btn-white" onClick={() => handleEditClick(row)}>
          <i style={{ color: 'black' }} className="bi bi-pencil-square"></i>
        </button>
      ),
    },
  ];

  const renderCompactPagination = () => {
    const pageButtons = [];
    const maxVisible = 3;

    // Previous
    pageButtons.push(
      <button
        key="prev"
        className="btn btn-sm btn-outline-secondary mx-1"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(p => p - 1)}
      >
        Previous
      </button>
    );

    // First few pages
    const start = 1;
    const end = Math.min(maxVisible, totalPages);
    for (let i = start; i <= end; i++) {
      pageButtons.push(
        <button
          key={i}
          className={`btn btn-sm mx-1 ${currentPage === i ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    // Ellipsis and last page
    if (totalPages > maxVisible + 1 && currentPage < totalPages - 1) {
      pageButtons.push(<span key="dots" className="mx-2">...</span>);
    }

    if (totalPages > maxVisible && currentPage < totalPages - 1) {
      pageButtons.push(
        <button
          key={totalPages}
          className={`btn btn-sm mx-1 ${currentPage === totalPages ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    // Next
    pageButtons.push(
      <button
        key="next"
        className="btn btn-sm btn-outline-secondary mx-1"
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(p => p + 1)}
      >
        Next
      </button>
    );

    return <div className="d-flex justify-content-end align-items-center mt-3">{pageButtons}</div>;
  };

  return (
    <div className="section-body">
      <div className="container-fluid">
        <ToastContainer position="bottom-right" autoClose={3000} transition={Slide} />

        <div className="mb-3 d-flex justify-content-end">
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Hide Form' : 'Create Job'}
          </button>
        </div>

        {showForm && (
          <div className="card mb-4">
            <div className="card-header">
              <h3 className="card-title d-flex align-items-center mb-0" style={{ color: "#1d3a8a", fontWeight: 600 }}>
                <i className="fa fa-briefcase mr-2" style={{ fontSize: "20px" }} />
                Create Job
              </h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="form-group required-tooltip">
                      <label>Company</label>
                      <select name="company_id" value={job.company_id} onChange={handleChange} className="form-control required-input" >
                        <option value="">Select Company</option>
                        {companies.map(c => (
                          <option key={c.company_id} value={c.company_id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group required-tooltip">
                      <label>Job Title</label>
                      <input type="text" name="job_title" value={job.job_title} onChange={handleChange} className="form-control required-input"  />
                    </div>
                  </div>
                 <div className="col-md-6 mb-3">
  <div className="form-group required-tooltip">
  <label>Designation</label>

  <Select
    name="designation_id"
    classNames={{
      control: () => "required-select required-tooltip"
    }}
    options={designations.map(d => ({
      value: d.designation_id,
      label: d.designation_name
    }))}
    value={
      designations
        .map(d => ({
          value: d.designation_id,
          label: d.designation_name
        }))
        .find(opt => opt.value === job.designation_id) || null
    }
    onChange={(selected) =>
      setJob(prev => ({
        ...prev,
        designation_id: selected ? selected.value : ""
      }))
    }
    placeholder="Search designation..."
    isClearable
  />
  </div>
</div>

                  <div className="col-md-6 mb-3">
                    <div className="form-group required-tooltip">
                      <label>Job Type</label>
                      <select name="job_type" onChange={handleChange} className="form-control required-input">
                       <option value=""></option>
                        <option value="1">Full Time</option>
                        <option value="2">Part Time</option>
                        <option value="3">Contract</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group required-tooltip">
                      <label>Job Vacancy</label>
                      <input type="number" name="job_vacancy" value={job.job_vacancy} onChange={handleChange} className="form-control required-input"  />
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group required-tooltip">
                      <label>Gender</label>
                      <select name="gender" value={job.gender} onChange={handleChange} className="form-control required-input">
                        <option value="Any">Any</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group required-tooltip">
                      <label>Minimum Experience</label>
                      <input type="text" name="minimum_experience" value={job.minimum_experience} onChange={handleChange} className="form-control required-input"  />
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group required-tooltip">
                      <label>Date of Closing</label>
                      <input type="date" name="date_of_closing" value={job.date_of_closing} onChange={handleChange} className="form-control required-input"  />
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label>Status</label>
                      <select name="status" value={job.status} onChange={handleChange} className="form-control">
                        <option value={1}>Active</option>
                        <option value={0}>Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <div className="form-group required-tooltip">
                      <label>Short Description</label>
                      <textarea name="short_description" value={job.short_description} onChange={handleChange} className="form-control required-input" />
                    </div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <div className="form-group required-tooltip">
                      <label>Long Description</label>
                      <textarea name="long_description" value={job.long_description} onChange={handleChange} className="form-control required-input" />
                    </div>
                  </div>
                  <div className="col-md-12 text-end">
                    <button type="submit" className="btn btn-primary" disabled={isSaving}>
                      {isSaving ? 'Creating...' : 'Create Job'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
         {showEditModal && editJob && (
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Job</h5>
<button type="button" className="btn btn-sm btn-danger" onClick={() => setShowEditModal(false)}>
  &times;
</button>
                </div>
                <div className="modal-body">
                  <form>
                    <div className="mb-3">
                      <label>Job Title</label>
                      <input type="text" name="job_title" value={editJob.job_title || ''} onChange={handleEditChange} className="form-control" />
                    </div>
                    <div className="mb-3">
                      <label>Company</label>
                      <select name="company_id" value={editJob.company_id || ''} onChange={handleEditChange} className="form-control">
                        <option value="">Select Company</option>
                        {companies.map(c => (
                          <option key={c.company_id} value={c.company_id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label>Designation</label>
                      <select name="designation_id" value={editJob.designation_id || ''} onChange={handleEditChange} className="form-control">
                        <option value="">Select Designation</option>
                        {designations.map(d => (
                          <option key={d.designation_id} value={d.designation_id}>{d.designation_name}</option>
                        ))}
                      </select>
                    </div>
                    
                    
                    <div className="mb-3">
                      <label>Status</label>
                      <select name="status" value={editJob.status} onChange={handleEditChange} className="form-control">
                        <option value={1}>Active</option>
                        <option value={0}>Inactive</option>
                      </select>
                    </div>
                    <div className="text-end">
                      <button type="button" className="btn btn-primary" onClick={submitEdit} disabled={isUpdating}>
                        {isUpdating ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

      


        {/* Job Table */}
       <div className="card">
<div className="card-header">
  <h3 className="card-title d-flex align-items-center" style={{ color: "#1d3a8a", fontWeight: 600 }}>
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="#1d3a8a" style={{ marginRight: "8px", verticalAlign: "-3px" }}>
      <path d="M6.5 1A1.5 1.5 0 0 0 5 2.5V3H1.5A1.5 1.5 0 0 0 0 4.5v8A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-8A1.5 1.5 0 0 0 14.5 3H11v-.5A1.5 1.5 0 0 0 9.5 1zm0 1h3a.5.5 0 0 1 .5.5V3H6v-.5a.5.5 0 0 1 .5-.5m1.886 6.914L15 7.151V12.5a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5V7.151l6.614 1.763a1.5 1.5 0 0 0 .772 0M1.5 4h13a.5.5 0 0 1 .5.5v1.616L8.129 7.948a.5.5 0 0 1-.258 0L1 6.116V4.5a.5.5 0 0 1 .5-.5"/>
    </svg>
    Job Post
  </h3>
</div>
<div className="p-3 border-bottom bg-light">
  <div className="d-flex justify-content-between align-items-center">
    <div className="d-flex align-items-center gap-4">
      <DownloadButtons data={filteredJobs} filename="jobs_report" />
    </div>

    {/* Right: rows-per-page + search box */}
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <EntriesSelector
        entriesPerPage={entriesPerPage}
        setEntriesPerPage={setEntriesPerPage}
        setCurrentPage={setCurrentPage}
      />

      <div style={{ width: '250px', marginLeft: '15px' }}>
        <input
          type="text"
          placeholder="Search by title..."
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="form-control"
        />
      </div>
    </div>
  </div>
</div>


  <div className="card-header">
    <h5 className="card-title">Recently Created Jobs</h5>
  </div>
  <div className="card-body">
            <DataTable
              columns={jobColumns}
              data={currentJobs}
              highlightOnHover
              striped
              responsive
              noDataComponent="No jobs found."
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
</div>
  );
};

export default CreateJob;